import { attachmentApi, folderApi, handleError } from '@ardoq/api';
import {
  APIAttachment,
  ArdoqId,
  DocumentArchiveContext,
} from '@ardoq/api-types';
import { setStateProperty } from '@ardoq/common-helpers';
import { toIds, toIdSet } from '@ardoq/core';
import { showImagePreviewModal } from '@ardoq/image-preview';
import {
  getImagePreviewErrorMessages,
  getImagePreviewLabels,
  getImagePreviewModalChrome,
} from '../locales/getImagePreviewLabels';
import { downloadFile } from './downloadFile';
import {
  action$,
  collectRoutines,
  connect,
  dispatchAction,
  extractPayload,
  ofType,
  reducedStream,
  reducer,
  spreadMap,
} from '@ardoq/rxbeach';
import {
  AbsoluteScrollPosition,
  CellCoordinate,
  ScrollPosition,
  SortDescriptor,
} from '@ardoq/table';
import {
  combineLatest,
  delay,
  filter,
  from,
  map,
  mergeMap,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { featureContext$ } from '../streams/featureContext$';
import { currentUserPermissionContext$ } from '../streams/currentUserPermissions/currentUserPermissionContext$';
import {
  closeDuplicatesModal,
  findDuplicates,
  toggleDuplicateSelected,
} from './duplicates/actions';
import { duplicates$, duplicatesRoutines } from './duplicates/duplicates$';
import {
  DuplicatesCommands,
  toDocumentArchiveDuplicatesProps,
} from './duplicates/toDuplicatesModalProps';
import { documentArchiveAttachments$ } from '../streams/documentArchive/documentArchiveAttachments$';
import { documentArchiveFolders$ } from '../streams/documentArchive/documentArchiveFolders$';
import { i18nLocale$ } from '../streams/i18nLocale$';
import { locale$ } from '../streams/locale$';
import { currentOrganizationId$ } from '../streams/organizations/currentOrganizationId$';
import { trackingData } from '../tracking/trackPayload';
import {
  cancelDelete,
  clearHighlightedRows,
  closeMoveToModal,
  confirmDelete,
  confirmMoveToNewFolder,
  ConfirmMoveToNewFolderPayload,
  createFolder,
  deleteAttachments,
  deleteFolders,
  DeleteRequest,
  downloadAttachment,
  FilterExpression,
  highlightRows,
  moveAttachments,
  MoveAttachmentsPayload,
  moveFolder,
  MoveFolderPayload,
  openMoveToModal,
  OpenMoveToModalPayload,
  renameAttachment,
  renameFolder,
  requestDeletion,
  setEditedCell,
  setFilterExpressions,
  setFolderClosed,
  setFolderExpanded,
  setMoveToModalDestination,
  setMoveToModalNewFolderName,
  setMoveToModalSearchTerm,
  setScrollPosition,
  setSearchTerm,
  setSelectedIds,
  setSortDescriptor,
  showDragOverlay,
  attachmentRenameRejected,
  uploadFiles,
  UploadFilesPayload,
} from './actions';
import {
  areFileSizesValid,
  toUploadFilesRejections,
} from './attachmentUploadUtils';
import {
  type DocumentArchiveDuplicatesProps,
  DocumentArchivePageView,
  type DocumentArchivePageViewProps,
  type DocumentArchiveViewProps,
} from './DocumentArchivePageView';
import {
  DocumentArchiveCommands,
  toDocumentArchivePageViewProps,
} from './toDocumentArchivePageViewProps';

export {
  areFileSizesValid,
  MAX_FILE_SIZE,
  toUploadFilesRejections,
} from './attachmentUploadUtils';

export const globalDocumentArchiveAttachments$ =
  documentArchiveAttachments$.pipe(
    map(s => s.models),
    map(attachments =>
      attachments.filter(a => a.context === DocumentArchiveContext.ORG)
    )
  );
export const globalDocumentArchiveFolders$ = documentArchiveFolders$.pipe(
  map(s => s.models),
  map(folders => folders.filter(f => f.context === DocumentArchiveContext.ORG))
);

const handleSetFolderExpanded = (
  state: DocumentArchiveState,
  folderId: string
): DocumentArchiveState => {
  if (state.expandedIds.includes(folderId)) {
    return state;
  }
  return {
    ...state,
    expandedIds: [...state.expandedIds, folderId],
  };
};

const handleSetFolderClosed = (
  state: DocumentArchiveState,
  folderId: string
): DocumentArchiveState => ({
  ...state,
  expandedIds: state.expandedIds.filter(id => id !== folderId),
});

const handleSetSearchTerm = (
  state: DocumentArchiveState,
  searchTerm: string
): DocumentArchiveState => ({
  ...state,
  searchTerm,
  expandedIds: searchTerm ? state.expandedIds : [],
});

const handleSetFilterExpressions = (
  state: DocumentArchiveState,
  filterExpressions: FilterExpression[]
): DocumentArchiveState => {
  const cleanedFilterExpressions = filterExpressions.filter(
    fe => fe.selection.length > 0
  );

  return {
    ...state,
    filterExpressions: cleanedFilterExpressions,
  };
};

const handleRequestDeletion = (
  state: DocumentArchiveState,
  payload: DeleteRequest
): DocumentArchiveState => ({
  ...state,
  deleteRequest: {
    open: true,
    folderIds: payload.folderIds,
    attachmentIds: payload.attachmentIds,
  },
});

const closeDeleteRequest = (
  state: DocumentArchiveState
): DocumentArchiveState => ({
  ...state,
  deleteRequest: {
    ...state.deleteRequest,
    open: false,
  },
});

const handleOpenMoveToModal = (
  state: DocumentArchiveState,
  payload: OpenMoveToModalPayload
): DocumentArchiveState => ({
  ...state,
  moveToModal: {
    open: true,
    folderIds: payload.folderIds,
    attachmentIds: payload.attachmentIds,
    selectedDestinationFolderId: 'root',
    searchTerm: '',
    newFolderName: null,
  },
});

const handleCloseMoveToModal = (
  state: DocumentArchiveState
): DocumentArchiveState => ({
  ...state,
  moveToModal: {
    ...state.moveToModal,
    open: false,
  },
});

const handleSetMoveToModalDestination = (
  state: DocumentArchiveState,
  selectedDestinationFolderId: string | 'root'
): DocumentArchiveState => ({
  ...state,
  moveToModal: {
    ...state.moveToModal,
    selectedDestinationFolderId,
  },
});

const handleSetMoveToModalSearchTerm = (
  state: DocumentArchiveState,
  searchTerm: string
): DocumentArchiveState => ({
  ...state,
  moveToModal: {
    ...state.moveToModal,
    searchTerm,
  },
});

const handleSetMoveToModalNewFolderName = (
  state: DocumentArchiveState,
  newFolderName: string | null
): DocumentArchiveState => ({
  ...state,
  moveToModal: {
    ...state.moveToModal,
    newFolderName,
  },
});

const handleHighlightRows = (
  state: DocumentArchiveState,
  rowIds: string[]
): DocumentArchiveState => ({
  ...state,
  highlightedRows: [...state.highlightedRows, ...rowIds],
});

const handleClearHighlightedRow = (
  state: DocumentArchiveState,
  rowIds: string[]
): DocumentArchiveState => ({
  ...state,
  highlightedRows: state.highlightedRows.filter(id => !rowIds.includes(id)),
});

export type DocumentArchiveState = {
  expandedIds: string[];
  selectedIds: string[];
  editedCell: CellCoordinate | null;
  filterExpressions: FilterExpression[];
  searchTerm: string;
  isDragging: boolean;
  sortDescriptor: SortDescriptor | null;
  scrollPosition: ScrollPosition;
  highlightedRows: string[];
  deleteRequest: {
    open: boolean;
    folderIds: string[];
    attachmentIds: string[];
  };
  moveToModal: {
    open: boolean;
    folderIds: string[];
    attachmentIds: string[];
    selectedDestinationFolderId: string | 'root';
    searchTerm: string;
    newFolderName: string | null;
  };
};

export const documentArchiveState$ = reducedStream<DocumentArchiveState>(
  'documentArchiveState$',
  {
    expandedIds: [],
    selectedIds: [],
    editedCell: null,
    filterExpressions: [],
    searchTerm: '',
    isDragging: false,
    sortDescriptor: null,
    scrollPosition: { top: 0, left: 0 },
    deleteRequest: {
      open: false,
      folderIds: [],
      attachmentIds: [],
    },
    highlightedRows: [],
    moveToModal: {
      open: false,
      folderIds: [],
      attachmentIds: [],
      selectedDestinationFolderId: 'root',
      searchTerm: '',
      newFolderName: null,
    },
  },
  [
    reducer(setFolderExpanded, handleSetFolderExpanded),
    reducer(setFolderClosed, handleSetFolderClosed),
    reducer(setSelectedIds, setStateProperty('selectedIds')),
    reducer(setEditedCell, setStateProperty('editedCell')),
    reducer(setFilterExpressions, handleSetFilterExpressions),
    reducer(setSearchTerm, handleSetSearchTerm),
    reducer(showDragOverlay, setStateProperty('isDragging')),
    reducer(setSortDescriptor, setStateProperty('sortDescriptor')),
    reducer(requestDeletion, handleRequestDeletion),
    reducer(cancelDelete, closeDeleteRequest),
    reducer(confirmDelete, closeDeleteRequest),
    reducer(setScrollPosition, setStateProperty('scrollPosition')),
    reducer(highlightRows, handleHighlightRows),
    reducer(clearHighlightedRows, handleClearHighlightedRow),
    reducer(openMoveToModal, handleOpenMoveToModal),
    reducer(closeMoveToModal, handleCloseMoveToModal),
    reducer(setMoveToModalDestination, handleSetMoveToModalDestination),
    reducer(setMoveToModalSearchTerm, handleSetMoveToModalSearchTerm),
    reducer(setMoveToModalNewFolderName, handleSetMoveToModalNewFolderName),
  ]
);

const queueClearHighlight = action$.pipe(
  ofType(highlightRows),
  extractPayload(),
  delay(1000),
  tap(rowIds => {
    dispatchAction(clearHighlightedRows(rowIds));
  })
);

const handleMoveFolder = action$.pipe(
  ofType(moveFolder),
  extractPayload(),
  switchMap(({ typeId, foldersWithNewParent }) =>
    folderApi.moveMultipleToFolder({
      type: 'org',
      typeId,
      foldersWithNewParent,
    })
  ),
  handleError()
);

const handleMoveAttachments = action$.pipe(
  ofType(moveAttachments),
  extractPayload(),
  switchMap(({ attachmentIds, folder }) =>
    attachmentApi.moveMultipleToFolder({ attachmentIds, folder })
  ),
  handleError()
);

const handleRenameFolder = action$.pipe(
  ofType(renameFolder),
  extractPayload(),
  switchMap(payload =>
    folderApi.rename({
      type: 'org',
      typeId: payload.typeId,
      renameRequest: {
        _id: payload.id,
        _version: payload.version,
        name: payload.newName,
      },
    })
  ),
  handleError()
);

const handleRenameAttachment = action$.pipe(
  ofType(renameAttachment),
  extractPayload(),
  switchMap(payload =>
    attachmentApi.rename({
      type: 'org',
      typeId: payload.typeId,
      renameRequest: {
        _id: payload.id,
        _version: payload.version,
        name: payload.newName,
      },
    })
  ),
  handleError()
);

const handleConfirmDelete = action$.pipe(
  ofType(confirmDelete),
  extractPayload(),
  tap(payload => {
    dispatchAction(deleteFolders(payload.folderIds));
    dispatchAction(deleteAttachments(payload.attachmentIds));
  })
);

const handleDeleteFolder = action$.pipe(
  ofType(deleteFolders),
  extractPayload(),
  switchMap(folderApi.deleteMultiple),
  handleError()
);

const handleDeleteAttachments = action$.pipe(
  ofType(deleteAttachments),
  extractPayload(),
  switchMap(attachmentApi.deleteMultiple),
  handleError()
);

const handleDownloadAttachment = action$.pipe(
  ofType(downloadAttachment),
  extractPayload(),
  map(({ uri }) => downloadFile(uri))
);

// The folder isn't added to the folders$ immediately after creation,
// so we need to wait for it to appear there before proceeding in order to successfully enter edit mode.
const waitForFolderToExist$ = (folderId: string) =>
  globalDocumentArchiveFolders$.pipe(
    filter(folders => folders.some(f => f._id === folderId)),
    take(1),
    map(() => folderId)
  );

const waitForAttachmentToExist$ = (uploadedAttachments: APIAttachment[]) => {
  const uploadedIds = toIdSet(uploadedAttachments);

  return globalDocumentArchiveAttachments$.pipe(
    map(attachments => attachments.filter(a => uploadedIds.has(a._id))),
    filter(filtered => filtered.length === uploadedAttachments.length),
    take(1)
  );
};

const handleCreateFolder = action$.pipe(
  ofType(createFolder),
  extractPayload(),
  switchMap(payload =>
    folderApi.create({
      name: payload.name,
      type: 'org',
      typeId: payload.typeId,
    })
  ),
  handleError(),
  switchMap(folder => waitForFolderToExist$(folder._id)),
  tap(folderId =>
    dispatchAction(setEditedCell({ rowId: folderId, columnId: '_group' }))
  )
);

const legalFileSizes = (payload: UploadFilesPayload): boolean =>
  areFileSizesValid(payload.files);

const notDuplicateNames = (payload: UploadFilesPayload): boolean =>
  !payload.files.some(file =>
    payload.existingAttachmentNames.includes(file.name)
  );

const handleUploadFiles = action$.pipe(
  ofType(uploadFiles),
  extractPayload(),
  filter(legalFileSizes),
  filter(notDuplicateNames),
  switchMap(payload =>
    attachmentApi.uploadFiles({
      files: payload.files,
      type: 'org',
      typeId: payload.typeId,
    })
  ),
  handleError(),
  switchMap(waitForAttachmentToExist$),
  tap(attachments => {
    dispatchAction(highlightRows(toIds(attachments)));
    dispatchAction(
      setScrollPosition({
        rowId: attachments[attachments.length - 1]._id,
        left: 0,
      })
    );
  })
);

const handleUploadFilesRejected = action$.pipe(
  ofType(uploadFiles),
  extractPayload(),
  mergeMap(toUploadFilesRejections),
  tap(dispatchAction)
);

const handleConfirmMoveToNewFolder = action$.pipe(
  ofType(confirmMoveToNewFolder),
  extractPayload(),
  switchMap(payload =>
    from(
      folderApi.create({
        name: payload.newFolderName,
        type: 'org',
        typeId: payload.typeId,
      })
    ).pipe(
      handleError(),
      switchMap(newFolder =>
        waitForFolderToExist$(newFolder._id).pipe(
          map(() => ({ folderId: newFolder._id, payload }))
        )
      )
    )
  ),
  tap(({ folderId, payload }) => {
    if (payload.foldersToMove.length > 0) {
      dispatchAction(
        moveFolder({
          typeId: payload.typeId,
          foldersWithNewParent: payload.foldersToMove.map(f => ({
            ...f,
            parent: folderId,
          })),
        })
      );
    }
    if (payload.attachmentsToMove.length > 0) {
      dispatchAction(
        moveAttachments({
          attachmentIds: toIds(payload.attachmentsToMove),
          folder: folderId,
        })
      );
    }
    dispatchAction(closeMoveToModal());
  })
);

export const globalDocumentArchiveCommands: DocumentArchiveCommands = {
  moveToModal: {
    openMoveToModal: (folderIds: string[], attachmentIds: string[]) =>
      dispatchAction(openMoveToModal({ folderIds, attachmentIds })),
    closeMoveToModal: () => dispatchAction(closeMoveToModal()),
    setMoveToModalDestination: (folderId: string | 'root') =>
      dispatchAction(setMoveToModalDestination(folderId)),
    setMoveToModalSearchTerm: (searchTerm: string) =>
      dispatchAction(setMoveToModalSearchTerm(searchTerm)),
    setMoveToModalNewFolderName: (name: string | null) =>
      dispatchAction(setMoveToModalNewFolderName(name)),
    confirmMoveToNewFolder: (payload: ConfirmMoveToNewFolderPayload) =>
      dispatchAction(confirmMoveToNewFolder(payload)),
  },
  dropZone: {
    showDragOverlay: (show: boolean) => dispatchAction(showDragOverlay(show)),
    uploadFiles: (
      files: File[],
      typeId: ArdoqId,
      existingAttachmentNames: string[],
      dropped: boolean
    ) =>
      dispatchAction(
        uploadFiles({
          files,
          typeId,
          existingAttachmentNames,

          [trackingData]: {
            event: 'Global document archive0 Uploaded files',
            payload: {
              numberOfFiles: files.length,
              dropped,
            },
          },
        })
      ),
  },
  deleteConfirmationModal: {
    confirmDelete: (folderIds: string[], attachmentIds: string[]) =>
      dispatchAction(
        confirmDelete({
          folderIds,
          attachmentIds,

          [trackingData]: {
            event: 'Global document archive0 Confirm delete',
            payload: {
              numberOfFolders: folderIds.length,
              numberOfAttachments: attachmentIds.length,
            },
          },
        })
      ),
    cancelDelete: () =>
      dispatchAction(
        cancelDelete({
          [trackingData]: {
            event: 'Global document archive0 Cancel delete',
          },
        })
      ),
  },
  documentArchiveTable: {
    setSearchTerm: (term: string) => dispatchAction(setSearchTerm(term)),
    setSortDescriptor: (descriptor: SortDescriptor | null) =>
      dispatchAction(setSortDescriptor(descriptor)),
    setFilterExpressions: (expressions: FilterExpression[]) =>
      dispatchAction(setFilterExpressions(expressions)),
    setFolderExpanded: (folderId: string) =>
      dispatchAction(setFolderExpanded(folderId)),
    setFolderClosed: (folderId: string) =>
      dispatchAction(setFolderClosed(folderId)),
    setSelectedIds: (ids: string[]) => dispatchAction(setSelectedIds(ids)),
    createFolder: (name: string, typeId: ArdoqId) =>
      dispatchAction(
        createFolder({
          name,
          typeId,

          [trackingData]: {
            event: 'Global document archive0 Created folder',
          },
        })
      ),
    renameAttachment: (
      id: string,
      newName: string,
      typeId: ArdoqId,
      version: number
    ) =>
      dispatchAction(
        renameAttachment({
          id,
          version,
          newName,
          typeId,

          [trackingData]: {
            event: 'Global document archive0 Renamed item',
            payload: {
              itemType: 'attachment',
            },
          },
        })
      ),
    rejectAttachmentRename: () => dispatchAction(attachmentRenameRejected()),
    renameFolder: (
      id: string,
      newName: string,
      typeId: ArdoqId,
      version: number
    ) =>
      dispatchAction(
        renameFolder({
          id,
          version,
          newName,
          typeId,

          [trackingData]: {
            event: 'Global document archive0 Renamed item',
            payload: {
              itemType: 'folder',
            },
          },
        })
      ),

    folderMenuItem: {
      setEditedCell: (coordinate: CellCoordinate | null) =>
        dispatchAction(setEditedCell(coordinate)),
      requestDeletion: (attachmentIds: string[], folderIds: string[]) =>
        dispatchAction(
          requestDeletion({
            folderIds,
            attachmentIds,
            [trackingData]: {
              event: 'Global document archive0 Request deletion',
            },
          })
        ),
      openMoveToModal: (folderIds: string[], attachmentIds: string[]) =>
        dispatchAction(openMoveToModal({ folderIds, attachmentIds })),
    },
    attachmentMenuItem: {
      showImagePreviewModal: (title: string, url: string) => {
        // i18nLocale$.state OK here: snapshot-at-open-time; @ardoq/modal renders modal() body once and does not re-render on locale change
        const i18nLocale = i18nLocale$.state;
        const { downloadLinkLabel, closeButtonText } =
          getImagePreviewModalChrome(i18nLocale);
        return showImagePreviewModal({
          title,
          url,
          link: { href: url, label: downloadLinkLabel },
          closeButtonText,
          labels: getImagePreviewLabels(i18nLocale),
          errorMessages: getImagePreviewErrorMessages(i18nLocale),
        });
      },
      setEditedCell: (coordinate: CellCoordinate | null) =>
        dispatchAction(setEditedCell(coordinate)),
      requestDeletion: (attachmentIds: string[], folderIds: string[]) =>
        dispatchAction(
          requestDeletion({
            folderIds,
            attachmentIds,
            [trackingData]: {
              event: 'Global document archive0 Request deletion',
            },
          })
        ),
      downloadAttachment: (uri: string) =>
        dispatchAction(
          downloadAttachment({
            uri,
            [trackingData]: {
              event: 'Global document archive0 Downloaded attachment',
            },
          })
        ),
      openMoveToModal: (folderIds: string[], attachmentIds: string[]) =>
        dispatchAction(openMoveToModal({ folderIds, attachmentIds })),
    },
    dragAndDroppable: {
      moveFolder: (payload: MoveFolderPayload) =>
        dispatchAction(
          moveFolder({
            ...payload,
            [trackingData]: {
              event: 'Global document archive0 Moved item',
              payload: {
                itemType: 'folder',
                numberOfItems: 1,
              },
            },
          })
        ),
      moveAttachments: (payload: MoveAttachmentsPayload) =>
        dispatchAction(
          moveAttachments({
            ...payload,
            [trackingData]: {
              event: 'Global document archive0 Moved item',
              payload: {
                itemType: 'attachment',
                numberOfItems: payload.attachmentIds.length,
              },
            },
          })
        ),
    },
    scrollable: {
      setScrollPosition: (scrollPosition: AbsoluteScrollPosition) =>
        dispatchAction(setScrollPosition(scrollPosition)),
    },
  },
};

export const documentArchiveRoutines = collectRoutines(
  handleRenameFolder,
  handleRenameAttachment,
  handleConfirmDelete,
  handleDeleteFolder,
  handleDeleteAttachments,
  handleDownloadAttachment,
  handleMoveFolder,
  handleMoveAttachments,
  handleCreateFolder,
  handleUploadFiles,
  handleUploadFilesRejected,
  queueClearHighlight,
  handleConfirmMoveToNewFolder
);

const duplicatesCommands: DuplicatesCommands = {
  findDuplicates: () => dispatchAction(findDuplicates()),
  toggleSelected: (id: ArdoqId) => dispatchAction(toggleDuplicateSelected(id)),
  close: () => dispatchAction(closeDuplicatesModal()),
  requestDeletion: (attachmentIds: ArdoqId[]) =>
    dispatchAction(
      requestDeletion({
        folderIds: [],
        attachmentIds,
        [trackingData]: {
          event: 'Global document archive0 Request deletion',
        },
      })
    ),
};

const toPageViewProps = (
  pageProps: DocumentArchiveViewProps,
  duplicates: DocumentArchiveDuplicatesProps
): DocumentArchivePageViewProps => ({ ...pageProps, duplicates });

export const DocumentArchivePage = connect(
  DocumentArchivePageView,
  combineLatest([
    combineLatest([
      documentArchiveState$,
      globalDocumentArchiveFolders$,
      globalDocumentArchiveAttachments$,
      currentOrganizationId$,
      locale$,
      of(globalDocumentArchiveCommands),
      i18nLocale$,
    ]).pipe(spreadMap(toDocumentArchivePageViewProps)),
    combineLatest([
      duplicates$,
      currentUserPermissionContext$,
      featureContext$,
      locale$,
      of(duplicatesCommands),
      i18nLocale$,
    ]).pipe(spreadMap(toDocumentArchiveDuplicatesProps)),
  ]).pipe(spreadMap(toPageViewProps)),
  collectRoutines(documentArchiveRoutines, duplicatesRoutines)
);
